import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import {
  addRole,
  deleteRole,
  fetchRoles,
  updatePermissions,
  updateRole,
} from "../../../actions/roleAction";
import { fetchPermissions } from "../../../actions/permissionAction";
import { AlertConfirmModal } from "../../../components/Modals/AlertConfirmModal";
import FormRole from "./FormRole";
import PermissionMatrix from "./PermissionMatrix";
import "./style.css";

const initialFormState = {
  id: "",
  name: "",
  description: "",
  permissions: [],
};

const isProtectedSystemRole = (roleName) =>
  ["owner", "super admin"].includes(
    String(roleName || "")
      .trim()
      .toLowerCase(),
  );

const idsFromRole = (role) => (role?.permissions || []).map((permission) => Number(permission.id)).sort((a, b) => a - b);
const idsSignature = (ids) => [...ids].map(Number).sort((a, b) => a - b).join(",");

const RolePage = ({
  roles = [],
  isLoading,
  fetchRoles,
  addRole,
  updateRole,
  updatePermissions,
  deleteRole,
  fetchPermissions,
  permissions = [],
  isLoadingPermission,
}) => {
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [baselinePermissionIds, setBaselinePermissionIds] = useState([]);
  const [roleSearch, setRoleSearch] = useState("");
  const [formState, setFormState] = useState(initialFormState);
  const [isShowModal, setIsShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [pendingRoleId, setPendingRoleId] = useState(null);
  const hydratedRoleRef = useRef(null);

  useEffect(() => {
    fetchRoles(250, 1);
    fetchPermissions(300, 1);
  }, [fetchPermissions, fetchRoles]);

  const selectedRole = useMemo(
    () => roles.find((role) => Number(role.id) === Number(selectedRoleId)) || null,
    [roles, selectedRoleId],
  );

  const filteredRoles = useMemo(() => {
    const keyword = roleSearch.trim().toLowerCase();
    if (!keyword) return roles;
    return roles.filter((role) => `${role.name || ""} ${role.description || ""}`.toLowerCase().includes(keyword));
  }, [roleSearch, roles]);

  const isDirty = idsSignature(selectedPermissionIds) !== idsSignature(baselinePermissionIds);
  const readOnly = isProtectedSystemRole(selectedRole?.name);
  const selectedCount = selectedPermissionIds.length;

  useEffect(() => {
    if (!roles.length) {
      setSelectedRoleId(null);
      return;
    }

    const roleStillExists = roles.some((role) => Number(role.id) === Number(selectedRoleId));
    if (!selectedRoleId || !roleStillExists) {
      const defaultRole = roles.find((role) => !isProtectedSystemRole(role.name)) || roles[0];
      hydratedRoleRef.current = null;
      setSelectedRoleId(defaultRole.id);
    }
  }, [roles, selectedRoleId]);

  useEffect(() => {
    if (!selectedRole || Number(hydratedRoleRef.current) === Number(selectedRole.id)) return;
    const permissionIds = idsFromRole(selectedRole);
    setSelectedPermissionIds(permissionIds);
    setBaselinePermissionIds(permissionIds);
    hydratedRoleRef.current = selectedRole.id;
  }, [selectedRole]);

  const selectRole = (roleId) => {
    hydratedRoleRef.current = null;
    setPendingRoleId(null);
    setSelectedRoleId(roleId);
  };

  const requestRoleSelection = (roleId) => {
    if (Number(roleId) === Number(selectedRoleId)) return;
    if (isDirty) {
      setPendingRoleId(roleId);
      return;
    }
    selectRole(roleId);
  };

  const openAddRole = () => {
    setFormState(initialFormState);
    setIsEdit(false);
    setIsShowModal(true);
  };

  const openEditRole = () => {
    if (!selectedRole || readOnly) return;
    setFormState({
      id: selectedRole.id,
      name: selectedRole.name,
      description: selectedRole.description || "",
      permissions: selectedRole.permissions || [],
    });
    setIsEdit(true);
    setIsShowModal(true);
  };

  const closeModal = () => {
    setFormState(initialFormState);
    setIsShowModal(false);
    setIsEdit(false);
  };

  const handleAdd = async () => {
    const success = await addRole(formState);
    if (success) closeModal();
    return success;
  };

  const handleUpdateRole = async () => {
    const success = await updateRole(formState.id, formState);
    if (success) closeModal();
    return success;
  };

  const handleSavePermissions = async () => {
    if (!selectedRole || readOnly || !isDirty) return false;
    const selected = new Set(selectedPermissionIds.map(Number));
    const payload = {
      permissions: permissions
        .filter((permission) => selected.has(Number(permission.id)))
        .map((permission) => ({ id: Number(permission.id), name: permission.name })),
    };
    const success = await updatePermissions(selectedRole.id, payload);
    if (success) {
      const savedIds = [...selectedPermissionIds].sort((a, b) => a - b);
      setBaselinePermissionIds(savedIds);
    }
    return success;
  };

  const discardPermissionChanges = () => {
    setSelectedPermissionIds([...baselinePermissionIds]);
  };

  const handleDelete = async () => {
    if (!roleToDelete) return false;
    const deletedId = roleToDelete.id;
    const result = await deleteRole(deletedId);
    setRoleToDelete(null);
    if (Number(selectedRoleId) === Number(deletedId)) {
      hydratedRoleRef.current = null;
      setSelectedRoleId(null);
    }
    return result;
  };

  const roleButtonModalProps = (roleId) =>
    isDirty && Number(roleId) !== Number(selectedRoleId)
      ? { "data-bs-toggle": "modal", "data-bs-target": "#modal-discard-role-changes" }
      : {};

  return (
    <div className="page-wrapper role-access-page">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h1 className="page-title fs-1">Role & Hak Akses</h1>
              <div className="page-pretitle">Atur akses pengguna dengan jelas untuk setiap modul AKAS.</div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <button
                type="button"
                className="btn btn-primary"
                data-bs-toggle="modal"
                data-bs-target="#modal-form"
                onClick={openAddRole}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                <span>Tambah Role</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="role-access-shell">
            <aside className="role-selector-panel">
              <div className="role-selector-header">
                <div className="role-selector-title">
                  <div>
                    <h3>Daftar Role</h3>
                    <p>Pilih role yang ingin diatur.</p>
                  </div>
                  <span className="role-count-badge">{roles.length}</span>
                </div>
                <div className="role-selector-search input-icon">
                  <span className="input-icon-addon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="7" />
                      <path d="m20 20-4-4" />
                    </svg>
                  </span>
                  <input
                    type="search"
                    className="form-control form-control-sm"
                    value={roleSearch}
                    onChange={(event) => setRoleSearch(event.target.value)}
                    placeholder="Cari role..."
                    aria-label="Cari role"
                  />
                </div>
              </div>

              <div className="role-selector-list">
                {isLoading && !roles.length
                  ? [...Array(5)].map((_, index) => (
                      <div className="role-selector-item" key={index}>
                        <span className="placeholder placeholder-lg w-50" />
                        <p><span className="placeholder placeholder-sm w-75" /></p>
                        <span className="placeholder placeholder-xs w-25" />
                      </div>
                    ))
                  : filteredRoles.map((role) => {
                      const active = Number(role.id) === Number(selectedRoleId);
                      const protectedRole = isProtectedSystemRole(role.name);
                      return (
                        <button
                          type="button"
                          className={`role-selector-item ${active ? "is-active" : ""}`}
                          key={role.id}
                          onClick={() => requestRoleSelection(role.id)}
                          {...roleButtonModalProps(role.id)}>
                          <div className="role-selector-item-top">
                            <strong>{role.name}</strong>
                            {protectedRole && <span className="role-system-label">Sistem</span>}
                          </div>
                          <p>{role.description || "Role pengguna AKAS"}</p>
                          <div className="role-selector-item-meta">
                            <span>{role.permissions?.length || 0} akses aktif</span>
                            {active && <span>Dipilih</span>}
                          </div>
                        </button>
                      );
                    })}
              </div>
            </aside>

            <main className="role-permission-panel">
              <div className="role-mobile-selector">
                {roles.map((role) => (
                  <button
                    type="button"
                    key={role.id}
                    className={`role-mobile-chip ${Number(role.id) === Number(selectedRoleId) ? "is-active" : ""}`}
                    onClick={() => requestRoleSelection(role.id)}
                    {...roleButtonModalProps(role.id)}>
                    {role.name}
                  </button>
                ))}
              </div>

              {selectedRole ? (
                <>
                  <header className="role-permission-header">
                    <div className="role-permission-heading">
                      <span className="role-permission-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 3 5 6v5c0 4.6 2.8 8.2 7 10 4.2-1.8 7-5.4 7-10V6l-7-3Z" />
                          <path d="m9 12 2 2 4-4" />
                        </svg>
                      </span>
                      <div>
                        <h2>{selectedRole.name}</h2>
                        <p>{selectedCount} dari {permissions.length} akses dipilih</p>
                      </div>
                    </div>
                    {!readOnly && (
                      <div className="role-permission-actions">
                        <button
                          type="button"
                          className="btn btn-sm"
                          data-bs-toggle="modal"
                          data-bs-target="#modal-form"
                          onClick={openEditRole}
                          aria-label={`Edit role ${selectedRole.name}`}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" />
                          </svg>
                          <span>Edit</span>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          data-bs-toggle="modal"
                          data-bs-target="#modal-delete-role"
                          onClick={() => setRoleToDelete(selectedRole)}
                          aria-label={`Hapus role ${selectedRole.name}`}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 7h16M10 11v6m4-6v6M6 7l1 13h10l1-13M9 7V4h6v3" />
                          </svg>
                          <span>Hapus</span>
                        </button>
                      </div>
                    )}
                  </header>

                  {readOnly && (
                    <div className="role-readonly-notice">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="5" y="11" width="14" height="9" rx="2" />
                        <path d="M8 11V8a4 4 0 0 1 8 0v3" />
                      </svg>
                      Role sistem dilindungi. Hak aksesnya dapat dilihat, tetapi dikelola otomatis oleh AKAS.
                    </div>
                  )}

                  <PermissionMatrix
                    permissions={permissions}
                    selectedIds={selectedPermissionIds}
                    onChange={setSelectedPermissionIds}
                    readOnly={readOnly}
                    loading={isLoadingPermission}
                  />

                  {!readOnly && isDirty && (
                    <div className="permission-save-bar">
                      <div className="permission-save-status">
                        <span>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 8v4l2 2" />
                            <circle cx="12" cy="12" r="9" />
                          </svg>
                        </span>
                        <div>
                          <strong>Perubahan belum disimpan</strong>
                          <small>{selectedCount} akses akan diterapkan untuk {selectedRole.name}.</small>
                        </div>
                      </div>
                      <div className="permission-save-actions">
                        <button type="button" className="btn btn-sm" onClick={discardPermissionChanges} disabled={isLoading}>
                          Batal
                        </button>
                        <button type="button" className="btn btn-primary btn-sm" onClick={handleSavePermissions} disabled={isLoading}>
                          {isLoading ? <span className="spinner-border spinner-border-sm" /> : (
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M5 4h12l2 2v14H5Z" />
                              <path d="M8 4v6h8V4M8 20v-6h8v6" />
                            </svg>
                          )}
                          Simpan Akses
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="permission-empty m-4">
                  <strong>Belum ada role yang dapat dipilih</strong>
                  <small>Tambahkan role baru untuk mulai mengatur hak akses.</small>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>

      <FormRole
        isShowModal={isShowModal}
        isEdit={isEdit}
        closeModal={closeModal}
        formState={formState}
        setFormState={setFormState}
        handleAdd={handleAdd}
        handleUpdateRole={handleUpdateRole}
      />

      <AlertConfirmModal
        type="error"
        title="Hapus role?"
        message={`Role ${roleToDelete?.name || "ini"} akan dihapus. Pengguna pada role tersebut perlu dipindahkan terlebih dahulu.`}
        confirmText="Hapus"
        cancelText="Batal"
        idModal="delete-role"
        idData={roleToDelete?.id}
        waitForConfirm
        onConfirm={handleDelete}
        onCancel={() => setRoleToDelete(null)}
      />

      <AlertConfirmModal
        type="warning"
        title="Buang perubahan akses?"
        message="Perubahan hak akses pada role ini belum disimpan. Jika dilanjutkan, perubahan tersebut akan dibatalkan."
        confirmText="Buang Perubahan"
        cancelText="Kembali"
        idModal="discard-role-changes"
        idData={pendingRoleId}
        onConfirm={(roleId) => selectRole(roleId)}
        onCancel={() => setPendingRoleId(null)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  roles: state?.roles?.datas?.data || [],
  isLoading: state?.roles?.loading,
  permissions: state?.permissions?.datas || [],
  isLoadingPermission: state?.permissions?.loading,
});

export default connect(mapStateToProps, {
  fetchRoles,
  fetchPermissions,
  addRole,
  updatePermissions,
  updateRole,
  deleteRole,
})(RolePage);
